import { Row, Spin, Modal, Button, Select } from "antd";
import React, { useContext, useEffect, useMemo, useState } from "react";
import TopSummary from "../../../components/TopSummary";
import "./styles.css";
import {
  LoadingOutlined,
  DownOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import ActionBar from "../../../components/ActionBar";
import BarChart from "./BarChart";
import { AppContext } from "../../../../context/AppContext";
import SubProjectsMap from "../../../SubProjectsMap";
import { useSubProjects } from "../../../../hooks/useProcuringEntity";
import useProcuringEntityStats from "../../../../hooks/useProcuringEntityStats";
import { isoDateToHumanReadableDate } from "../../../../Util";

// Responsive helper
const useIsMobileOrTablet = () => {
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    window.innerWidth <= 1024
  );
  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobileOrTablet;
};

const ProcuringEntity = (props) => {
  const {
    match: {
      params: { procuringEntityId },
    },
  } = props;

  const {
    setGoToOverviewPage,
    selectedProcuringEntity,
    projects,
    reports,
    selectedReportID,
    selectedProject,
    setSelectedProject,
    selectProcuringEntity,
    setSelectedReportID,
    loginUser,
  } = useContext(AppContext);

  const [filterSheetVisible, setFilterSheetVisible] = useState(false);

  const procuringEntity = procuringEntityId
    ? procuringEntityId
    : selectedProcuringEntity;

  const filterObj = useMemo(
    () => ({ "filter[procuring_entity_id]": procuringEntity }),
    [procuringEntity]
  );

  const { subProjects } = useSubProjects(filterObj);

  const {
    loading: loadingStats,
    physicalProgressData,
    financialProgressData,
    summaries,
  } = useProcuringEntityStats(procuringEntity);

  const findCouncilName = (procuringEntityId) => {
    for (const project of projects) {
      const entity = project.procuring_entities.find(
        (entity) => entity.procuring_entity_id === procuringEntityId
      );
      if (entity) {
        return entity.council_name;
      }
    }
    return null;
  };

  const capitalizeFirstLetter = (word) => {
    return word?.charAt(0)?.toUpperCase() + word?.slice(1);
  };

  const capitalizeWords = (str) => {
    return str
      ?.toLowerCase()
      ?.split(" ")
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(" ");
  };

  const councilName = findCouncilName(selectedProcuringEntity);

  useEffect(() => {
    setGoToOverviewPage(false);
  }, [procuringEntityId, setGoToOverviewPage]);

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 54,
      }}
      spin
    />
  );

  // Responsive detection
  const isMobileOrTablet = useIsMobileOrTablet();

  // Create options for filters
  const projectsOptions = projects?.map((project, index) => {
    return { value: project.id, label: project.name };
  });

  const procuringEntitiesOptions = (projectID) => {
    const sP = projects?.filter((project) => {
      return project.id === projectID;
    });
    const pEs = sP?.[0]?.procuring_entities;
    const options =
      pEs?.map((entity) => {
        return {
          value: entity?.procuring_entity_id,
          label: capitalizeFirstLetter(entity?.council_name),
        };
      }) || [];

    if (loginUser) {
      if (loginUser.councils && loginUser.councils.length > 0) {
        return loginUser.councils.map((council) => {
          return {
            value: council.id,
            label: council.name,
          };
        });
      }
    }
    return options;
  };

  const reportsIndex = reports?.map((report) => {
    return {
      value: report.id,
      label: `RN #${report.report_number}: ${isoDateToHumanReadableDate(
        report.start
      )} - ${isoDateToHumanReadableDate(report.end)}`,
    };
  });

  // Filter handlers
  const handleProject = (value) => {
    setSelectedProject(value);
    const sP = projects?.filter((project) => {
      return project.id === value;
    });

    if (loginUser) {
      if (loginUser.councils && loginUser.councils.length > 0) {
        selectProcuringEntity(
          loginUser.councils[0]?.id // Select first council by default
        );
      }
    }

    const pEs = sP?.[0]?.procuring_entities;
    if (pEs && pEs.length > 0) {
      selectProcuringEntity(pEs[0]?.procuring_entity_id);
    }
  };

  const handleProcuringEntity = (value) => {
    selectProcuringEntity(value);
  };

  const handleReports = (value) => {
    setSelectedReportID(value);
  };

  // Handle filter sheet close
  const handleFilterSheetClose = () => {
    setFilterSheetVisible(false);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    setFilterSheetVisible(false);
  };

  const currentReport = reports?.find((r) => r.id === selectedReportID);
  const currentProject = projects?.find((p) => p.id === selectedProject);
  const currentCouncil = findCouncilName(selectedProcuringEntity);

  return !loadingStats ? (
    <div className="ProcuringEntity">
      {/* ... existing action bar ... */}
      <div className="action-bar-overview-page">
        <ActionBar
          actionButtonProp={{
            title: selectedProcuringEntity
              ? `Overview: ${capitalizeWords(councilName)}`
              : "Overview",
          }}
          showBackButton={false}
        />
        {!isMobileOrTablet && <TopSummary summaries={summaries} />}
      </div>

      <section
        className={`ProcuringEntity-progress ${
          isMobileOrTablet ? "one-col-section" : ""
        }`}
      >
        <div
          className={`charts-container ${
            isMobileOrTablet ? "mobile-charts-container" : ""
          }`}
        >
          <BarChart
            data={physicalProgressData}
            title="Physical Progress"
            isGroup={true}
          />
          <BarChart
            data={financialProgressData}
            title="Financial Progress"
            isGroup={true}
          />
        </div>
        {/* TopSummary shown after charts in mobile/tablet view */}
        {isMobileOrTablet && <TopSummary summaries={summaries} />}
      </section>

      {/* ... map section ... */}
      <div
        className={`map-section ${isMobileOrTablet ? "one-col-section" : ""}`}
      >
        <SubProjectsMap subProjects={subProjects} />
      </div>
    </div>
  ) : (
    <Spin
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
      indicator={antIcon}
    />
  );
};

export default ProcuringEntity;
